import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Music, CalendarDays, Image, ChevronRight, Headphones, Disc3, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePlayer } from '../contexts/PlayerContext';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/dj-shadow-logo.png";
const HERO_BG = "https://images.unsplash.com/photo-1571266028243-e4733b0f0bb0?w=1920&q=80";
const DJ_PHOTO = "https://customer-assets.emergentagent.com/job_dj-dashboard-3/artifacts/28jcycbi_1AE93E6A-D385-4585-902A-D6963BBA78FC.jpg";

const Landing = () => {
  const { playMix } = usePlayer();
  const [latestMixes, setLatestMixes] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [about, setAbout] = useState({ bio: '', tagline: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [mixesRes, eventsRes, aboutRes] = await Promise.all([
        axios.get(`${API_URL}/mixes`),
        axios.get(`${API_URL}/events?upcoming=true`),
        axios.get(`${API_URL}/about`)
      ]);
      setLatestMixes(mixesRes.data.slice(0, 4));
      setUpcomingEvents(eventsRes.data.slice(0, 3));
      setAbout(aboutRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  return (
    <div className="min-h-screen">
      {/* DJ Background Graphics - Hidden on mobile to reduce GPU load */}
      <div className="dj-bg-pattern hidden md:block" />
      <div className="grid-lines hidden md:block" />
      
      {/* Floating Vinyl Records - Hidden on mobile */}
      <div className="vinyl-record vinyl-spin fixed w-64 h-64 -top-20 -left-20 opacity-20 hidden md:block" />
      <div className="vinyl-record vinyl-spin fixed w-96 h-96 -bottom-32 -right-32 opacity-10 hidden lg:block" style={{ animationDuration: '12s' }} />
      <div className="vinyl-record vinyl-spin fixed w-48 h-48 top-1/3 -right-16 opacity-15 hidden lg:block" style={{ animationDuration: '10s' }} />
      
      {/* Turntable Glows - Hidden on mobile */}
      <div className="turntable-glow fixed top-20 left-10 hidden md:block" />
      <div className="turntable-glow fixed bottom-40 right-20 hidden lg:block" style={{ animationDelay: '2s' }} />
      
      {/* Equalizer Bars - Hidden on mobile */}
      <div className="eq-bars fixed left-8 bottom-1/4 h-40 hidden md:block">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="eq-bar" style={{ animationDelay: `${i * 0.1}s` }} />
        ))}
      </div>
      
      {/* Equalizer Bars - Right Side - Hidden on mobile */}
      <div className="eq-bars fixed right-8 top-1/3 h-32 hidden lg:block">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="eq-bar" style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Hero Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_BG}
            alt="DJ Background"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090B] via-[#09090B]/80 to-[#09090B] z-10" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(217,249,157,0.15),transparent_70%)] z-10" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_20%,rgba(168,85,247,0.1),transparent_50%)] z-10" />
        </div>

        <div className="relative z-20 text-center px-6 max-w-5xl mx-auto">
          {/* DJ Photo with glow */}
          <div className="mb-6 animate-fade-in relative">
            <div className="absolute inset-0 bg-[#D9F99D]/20 blur-3xl rounded-full scale-150" />
            <img 
              src={LOGO_URL}
              alt="DJ Shadow"
              className="w-80 h-80 md:w-[420px] md:h-[420px] lg:w-[500px] lg:h-[500px] mx-auto object-contain relative z-10 drop-shadow-2xl"
            />
          </div>
          
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-extrabold tracking-tighter mb-6 animate-fade-in-delay-1 uppercase">
            DJ <span className="gradient-text">Shadow</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-zinc-300 mb-4 animate-fade-in-delay-2 font-light">
            @iamdjshadow_nyc
          </p>
          
          <p className="text-lg md:text-xl text-zinc-400 mb-10 animate-fade-in-delay-2 max-w-2xl mx-auto">
            {about.tagline || "NYC's hottest DJ bringing the heat to every party"}
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 animate-fade-in-delay-3">
            <Link to="/mixes">
              <Button 
                size="lg"
                className="bg-[#D9F99D] text-black hover:bg-[#BEF264] hover:shadow-[0_0_40px_-5px_rgba(217,249,157,0.8)] rounded-full px-10 py-7 text-lg font-bold uppercase tracking-wide transition-all duration-300 hover:scale-105"
                data-testid="listen-now-btn"
              >
                <Play className="w-6 h-6 mr-2" />
                Listen Now
              </Button>
            </Link>
            <Link to="/bookings">
              <Button 
                variant="outline"
                size="lg"
                className="bg-white/5 border-2 border-[#A855F7]/50 hover:bg-[#A855F7]/20 hover:border-[#A855F7] hover:shadow-[0_0_30px_-5px_rgba(168,85,247,0.6)] rounded-full px-10 py-7 text-lg font-bold uppercase tracking-wide transition-all duration-300"
                data-testid="book-now-btn"
              >
                <Sparkles className="w-6 h-6 mr-2" />
                Book Now
              </Button>
            </Link>
          </div>
        </div>

        {/* Audio Visualizer - Reduced bars on mobile for better performance */}
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex items-end gap-1 h-20 opacity-60">
          {[...Array(12)].map((_, i) => (
            <div 
              key={i}
              className="w-1.5 bg-gradient-to-t from-[#D9F99D] to-[#A855F7] rounded-full audio-bar hidden md:block"
              style={{ 
                animationDelay: `${i * 0.05}s`,
                height: `${Math.random() * 100}%`
              }}
            />
          ))}
          {/* Fewer bars visible on mobile */}
          {[...Array(8)].map((_, i) => (
            <div 
              key={`mobile-${i}`}
              className="w-1.5 bg-gradient-to-t from-[#D9F99D] to-[#A855F7] rounded-full audio-bar md:hidden"
              style={{ 
                animationDelay: `${i * 0.1}s`,
                height: `${Math.random() * 100}%`
              }}
            />
          ))}
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Latest Mixes Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Latest Mixes</h2>
              <p className="text-zinc-400 mt-1">Fresh sounds from the booth</p>
            </div>
            <Link to="/mixes">
              <Button variant="ghost" className="text-[#D9F99D]">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          {latestMixes.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {latestMixes.map((mix) => (
                <MixCard key={mix.id} mix={mix} onPlay={() => playMix(mix, latestMixes)} />
              ))}
            </div>
          ) : (
            <div className="glass-card rounded-2xl p-12 text-center">
              <Music className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">No mixes yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Upcoming Events</h2>
              <p className="text-zinc-400 mt-1">Catch me live</p>
            </div>
            <Link to="/events">
              <Button variant="ghost" className="text-[#D9F99D]">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          {upcomingEvents.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {upcomingEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <div className="glass-card rounded-2xl p-12 text-center">
              <CalendarDays className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">No upcoming events. Stay tuned!</p>
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      {about.bio && (
        <section className="py-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">About DJ Shadow</h2>
            <p className="text-lg text-zinc-400 leading-relaxed">{about.bio}</p>
          </div>
        </section>
      )}

      {/* Quick Links */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/mixes" className="group">
              <div className="glass-card rounded-2xl p-8 hover:border-[#D9F99D]/50 transition-all duration-300">
                <Music className="w-10 h-10 text-[#D9F99D] mb-4" />
                <h3 className="text-xl font-bold mb-2">All Mixes</h3>
                <p className="text-zinc-400">Browse the complete collection</p>
              </div>
            </Link>
            <Link to="/gallery" className="group">
              <div className="glass-card rounded-2xl p-8 hover:border-[#F472B6]/50 transition-all duration-300">
                <Image className="w-10 h-10 text-[#F472B6] mb-4" />
                <h3 className="text-xl font-bold mb-2">Photo Gallery</h3>
                <p className="text-zinc-400">Moments from the events</p>
              </div>
            </Link>
            <Link to="/calendar" className="group">
              <div className="glass-card rounded-2xl p-8 hover:border-[#8B5CF6]/50 transition-all duration-300">
                <CalendarDays className="w-10 h-10 text-[#8B5CF6] mb-4" />
                <h3 className="text-xl font-bold mb-2">Event Calendar</h3>
                <p className="text-zinc-400">Plan your nights out</p>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Mix Card Component
const MixCard = ({ mix, onPlay }) => {
  const { currentMix, isPlaying, togglePlay } = usePlayer();
  const isCurrentMix = currentMix?.id === mix.id;
  const isThisPlaying = isCurrentMix && isPlaying;

  const handleClick = () => {
    if (isCurrentMix) {
      togglePlay();
    } else {
      onPlay();
    }
  };

  return (
    <button
      onClick={handleClick}
      className="group text-left glass-card rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-300"
      data-testid={`mix-card-${mix.id}`}
    >
      <div className="aspect-square relative overflow-hidden bg-zinc-900">
        {mix.cover_url ? (
          <img 
            src={mix.cover_url} 
            alt={mix.title}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#27272A] to-[#18181B] flex items-center justify-center">
            <Headphones className="w-16 h-16 text-zinc-700" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-[#D9F99D] flex items-center justify-center">
            {isThisPlaying ? (
              <div className="flex items-end gap-0.5 h-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="w-1 bg-black rounded-full audio-bar" style={{ height: '100%' }} />
                ))}
              </div>
            ) : (
              <Play className="w-6 h-6 text-black ml-1" />
            )}
          </div>
        </div>
        {isThisPlaying && (
          <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-[#D9F99D] text-black text-xs font-bold">
            NOW PLAYING
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-bold truncate">{mix.title}</h3>
        <p className="text-zinc-400 text-sm">{mix.genre || 'Mix'}</p>
      </div>
    </button>
  );
};

// Event Card Component
const EventCard = ({ event }) => {
  const eventDate = new Date(event.date);
  const month = eventDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const day = eventDate.getDate();

  return (
    <div className="glass-card rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-300">
      <div className="p-6">
        <div className="flex gap-4">
          <div className="w-16 h-16 rounded-xl bg-[#D9F99D]/10 flex flex-col items-center justify-center flex-shrink-0">
            <span className="text-[#D9F99D] text-xs font-bold">{month}</span>
            <span className="text-2xl font-bold">{day}</span>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-lg truncate">{event.title}</h3>
            <p className="text-zinc-400 text-sm">{event.venue}</p>
            <p className="text-zinc-500 text-sm">{event.location}</p>
          </div>
        </div>
        {event.ticket_url && (
          <a 
            href={event.ticket_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 block w-full text-center py-2 rounded-full bg-white/5 hover:bg-white/10 text-sm font-medium transition-colors"
          >
            Get Tickets
          </a>
        )}
      </div>
    </div>
  );
};

export default Landing;
